//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { TypeHelper } from '../../../../../helpers'
import { IMRAccordionController, IIMRAccordion } from '../../accordion.controller'


// Public Types

export interface IIMRAccordionItem {
    isDisabled: boolean
    isExpanded: boolean
    isToolbarDisabled: boolean
    isToolbarVisible: boolean

    parent: IIMRAccordion

    collapse(): void
    disable(): void
    disableToolbar(): void
    enable(): void
    enableToolbar(): void
    expand(): void
}

export type IMRAccordionItemOptions = {
    disabled?: boolean
    expanded?: boolean

    toolbar?: {
        disabled?: boolean
        items?: any[]
        title?: string
        visible?: boolean
    },

    onInitialized?: (imrAccordionItem: IIMRAccordionItem) => void
}

// Public Types End


export class IMRAccordionItemController implements ng.IComponentController, IIMRAccordionItem {

    // Members

    private options: IMRAccordionItemOptions
    private parentController: IMRAccordionController
    private toolbar: DevExpress.ui.dxToolbar

    private _name: string
    private _toolbarOptions: DevExpress.ui.dxToolbarOptions
    private _isDisabled: boolean
    private _isExpanded: boolean
    private _isToolbarDisabled: boolean
    private _isToolbarVisible: boolean
    private _isInitialized: JQueryDeferred<void>

    private _needRepaint: boolean
    // Members End


    // IIMRAccordionItem Implementation

    public get isDisabled(): boolean {
        return this._isDisabled
    }

    public get isExpanded(): boolean {
        return this._isExpanded
    }

    public get isToolbarDisabled(): boolean {
        return this._isToolbarDisabled
    }

    public get isToolbarVisible(): boolean {
        return this._isToolbarVisible
    }

    public get parent(): IIMRAccordion {
        return this.parentController
    }

    public collapse(): void {
        this._isExpanded = false
        this.parentController.updateItems()
    }

    public disable(): void {
        this._isDisabled = true
        this.parentController.updateItems()
    }

    public disableToolbar(): void {
        if (this.toolbar != null) {
            this._isToolbarDisabled = true
            this.toolbar.option('disabled', true)
        }
    }

    public enable(): void {
        this._isDisabled = false
        this.parentController.updateItems()
    }

    public enableToolbar(): void {
        if (this.toolbar != null) {
            this._isToolbarDisabled = false
            this.toolbar.option('disabled', false)
        }
    }

    public expand(): void {
        this._isExpanded = true
        this.parentController.updateItems()
    }

    // IIMRAccordionItem Implementation End


    //#region Ctor

    public constructor() {
        this._isInitialized = $.Deferred<void>()
    }

    //#endregion


    // IComponentController Implementation 

    public $onInit(): void {
        this.parentController.registerAccordionItem(this)
    }

    public $onChanges(changes: { [property: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // IComponentController Implementation End

    //#region Lifecycle

    private initialize(): void {
        this.setDefaults()
        this.setOptions()

        if (this.options.toolbar != null && this.isToolbarVisible) {
            this.initializeToolbar().then(() => {
                this._isInitialized.resolve()
            })
        }

        this._isInitialized.resolve()
    }

    //#endregion


    // Parent/Child Communications

    public get isInitialized(): JQueryPromise<void> {
        return this._isInitialized.promise()
    }

    public set isExpanded(value: boolean) {
        this._isExpanded = value
    }

    public get name(): string {
        return this._name
    }

    public onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    public get toolbarOptions(): DevExpress.ui.dxToolbarOptions {
        return this._toolbarOptions
    }

    // Parent/Child Communications


    //#region Toolbar

    private initializeToolbar() {
        this._toolbarOptions = {
            disabled: this.isToolbarDisabled,
            items: this.options.toolbar.items || [],

            onInitialized: (e: any) => {
                this.toolbar = e.component
                this._needRepaint = true
            },
            onItemRendered: (e: any) => {
                if (this._needRepaint) {
                    setTimeout(() => {
                        e.component.repaint()
                        this._needRepaint = false
                    }, 200)
                }
            },

            // Stops collapsing/expanding of accordions on toolbar click
            onItemClick: (e: any) => e.event.stopPropagation()
        }

        if (this.options.toolbar.title != null) {
            this._toolbarOptions.items.unshift({
                location: 'before',
                text: this.options.toolbar.title
            })
        }

        return TypeHelper.onInitializedAll(this._toolbarOptions)
    }

    //#endregion

    // Options

    private setDefaults(): void {
        this._toolbarOptions = {}
        this._name = null
        this._isDisabled = false
        this._isExpanded = false
        this._isToolbarVisible = true
        this._isToolbarDisabled = false
    }

    private setOptions(): void {
        this._name = this.parentController.getNextAccordionItemName()

        if (this.options != null) {
            if (this.options.disabled != null) {
                this._isDisabled = this.options.disabled
            }

            if (this.options.expanded != null) {
                this._isExpanded = this.options.expanded
            }

            if (this.options.toolbar != null) {
                if (this.options.toolbar.visible != null) {
                    this._isToolbarVisible = this.options.toolbar.visible
                }

                if (this.options.toolbar.disabled != null) {
                    this._isToolbarDisabled = this.options.toolbar.disabled
                }
            }

        }
    }

    // Options End
}
